import React from 'react';
import { CardSize } from '../../hooks/useFieldLayout';
import Avatar from '../ui/Avatar';

export type PaymentStatus = 'confirmed' | 'cash' | 'transfer' | 'pending';

interface WebPlayerCardProps {
  name: string;
  position?: string;
  rating?: number;
  teamColor: string;
  size: CardSize;
  nameMaxChars: number;
  avatar?: string;
  isGuest?: boolean;
  goals?: number;
  assists?: number;
  saves?: number;
  isFinished?: boolean;
  isMVP?: boolean;
  onClick?: () => void;
  hasPendingReport?: boolean;
  hasApprovedReport?: boolean;
  hasRejectedReport?: boolean;
  hasUnsubmittedReport?: boolean;
  paymentStatus?: PaymentStatus;
  isCurrentUser?: boolean;
}

const SIZES = {
  sm: { width: 56, avatar: 24, nameFontSize: 8, starFontSize: 6, posFontSize: 6, padding: 4, gap: 1, radius: 6, badgeSize: 14 },
  md: { width: 64, avatar: 28, nameFontSize: 9, starFontSize: 7, posFontSize: 7, padding: 5, gap: 2, radius: 7, badgeSize: 16 },
  lg: { width: 72, avatar: 32, nameFontSize: 10, starFontSize: 8, posFontSize: 8, padding: 6, gap: 2, radius: 8, badgeSize: 18 },
};

const POSITION_MAP: Record<string, string> = {
  Arquero: 'ARQ',
  Defensor: 'DEF',
  Mediocampista: 'MC',
  Delantero: 'DEL',
};

const PAYMENT_CONFIG: Record<PaymentStatus, { label: string; bg: string; text: string }> = {
  confirmed: { label: '\u2713 Pag\u00f3', bg: '#00C853', text: '#fff' },
  cash: { label: 'Efectivo', bg: '#2196F3', text: '#fff' },
  transfer: { label: 'Validar pago', bg: '#F59E0B', text: '#fff' },
  pending: { label: 'Sin pagar', bg: 'rgba(255,255,255,0.07)', text: '#888' },
};

export const WebPlayerCard: React.FC<WebPlayerCardProps> = ({
  name,
  position,
  rating = 3,
  teamColor,
  size,
  nameMaxChars,
  avatar,
  isGuest = false,
  goals,
  assists,
  saves,
  isFinished = false,
  isMVP = false,
  onClick,
  hasPendingReport = false,
  hasApprovedReport = false,
  hasRejectedReport = false,
  hasUnsubmittedReport = false,
  paymentStatus,
  isCurrentUser = false,
}) => {
  const s = SIZES[size];
  const truncatedName = name.length > nameMaxChars ? name.slice(0, nameMaxChars) + '...' : name;
  const posAbbr = POSITION_MAP[position || ''] || position?.slice(0, 3).toUpperCase() || 'MC';
  const fullStars = Math.max(0, Math.min(5, Math.floor(rating)));

  // Badge priority: UNSUBMITTED > PENDING > REJECTED (APPROVED = no badge, reduces noise)
  const showBadge = hasUnsubmittedReport || hasPendingReport || hasRejectedReport;
  let badgeColor = '#F59E0B';
  let badgeContent = '!';
  if (hasPendingReport) {
    badgeColor = '#FF9800';
    badgeContent = '\u23F1';
  } else if (hasRejectedReport) {
    badgeColor = '#F44336';
    badgeContent = '\u2717';
  }

  // Show stats only when report is approved (like mobile)
  const showStats = isFinished && hasApprovedReport && (goals != null || assists != null || saves != null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        onClick={onClick}
        data-testid={isCurrentUser ? 'player-card-current-user' : undefined}
        style={{
          width: s.width,
          background: isGuest ? '#2a2a2a' : '#1a1a1a',
          borderRadius: s.radius,
          border: `2px solid ${isGuest ? '#555' : teamColor}`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: s.padding,
          gap: s.gap,
          cursor: onClick ? 'pointer' : 'default',
          boxShadow: isCurrentUser
            ? '0 0 0 3px #FFD700, 0 0 12px rgba(255,215,0,0.4)'
            : '0 2px 8px rgba(0,0,0,0.5)',
          position: 'relative',
          transition: 'transform 0.15s',
        }}
      >
        {/* MVP badge - top-left (like mobile) */}
        {isMVP && (
          <div style={{
            position: 'absolute', top: -5, left: -5,
            width: s.badgeSize, height: s.badgeSize, borderRadius: s.badgeSize / 2,
            background: '#FFD700', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: s.badgeSize * 0.6,
            border: '1px solid #fff',
            zIndex: 10,
          }}>
            <span role="img" aria-label="mvp">&#11088;</span>
          </div>
        )}

        {/* Stats report badge - top-right (like mobile) */}
        {showBadge && (
          <div style={{
            position: 'absolute', top: -5, right: -5,
            width: s.badgeSize, height: s.badgeSize, borderRadius: s.badgeSize / 2,
            background: badgeColor, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: s.badgeSize * 0.65, fontWeight: 700, color: '#fff',
            border: '1px solid #fff',
            zIndex: 10,
          }}>
            {badgeContent}
          </div>
        )}

        {/* Avatar (con fallback a iniciales si la imagen falla o no hay) */}
        <Avatar
          src={avatar}
          name={name}
          size={s.avatar}
          bg={isGuest ? '#444' : '#333'}
        />

        {/* Name */}
        <div style={{
          fontSize: s.nameFontSize, fontWeight: 700,
          color: isGuest ? '#888' : '#fff',
          textAlign: 'center', lineHeight: 1.1,
          whiteSpace: 'nowrap', overflow: 'hidden',
          width: '100%', textOverflow: 'ellipsis',
        }}>
          {truncatedName}
        </div>

        {/* Guest label */}
        {isGuest && (
          <div style={{
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 3,
            padding: '1px 3px',
          }}>
            <span style={{
              fontSize: Math.max(9, s.posFontSize - 1), fontWeight: 700,
              color: '#888', letterSpacing: 0.3,
            }}>
              INVITADO
            </span>
          </div>
        )}

        {/* Rating / Stats */}
        {showStats ? (
          <div style={{
            fontSize: s.starFontSize, color: '#B0BEC5',
            display: 'flex', gap: 3,
          }}>
            {goals != null && goals > 0 && <span>&#9917;{goals}</span>}
            {assists != null && assists > 0 && <span>&#127919;{assists}</span>}
            {saves != null && saves > 0 && <span>&#129526;{saves}</span>}
            {(goals === 0 || goals == null) && (assists === 0 || assists == null) && (saves === 0 || saves == null) && (
              <span style={{ color: '#555' }}>-</span>
            )}
          </div>
        ) : (
          <div style={{ fontSize: s.starFontSize, color: '#F59E0B', letterSpacing: -1 }}>
            {'\u2605'.repeat(fullStars)}{'\u2606'.repeat(5 - fullStars)}
          </div>
        )}

        {/* Position */}
        <div style={{
          fontSize: s.posFontSize, fontWeight: 600,
          color: isGuest ? '#666' : teamColor,
        }}>
          {posAbbr}
        </div>
      </div>

      {/* Payment status label (below card, like mobile) */}
      {paymentStatus && (
        <div style={{
          marginTop: 3,
          borderRadius: 4,
          padding: '2px 4px',
          width: s.width,
          textAlign: 'center',
          background: PAYMENT_CONFIG[paymentStatus].bg,
        }}>
          <span style={{
            fontSize: Math.max(9, s.posFontSize - 1),
            fontWeight: 600,
            color: PAYMENT_CONFIG[paymentStatus].text,
          }}>
            {PAYMENT_CONFIG[paymentStatus].label}
          </span>
        </div>
      )}
    </div>
  );
};
